import React from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Slider } from 'primereact/slider';
import { InputSwitch } from 'primereact/inputswitch';
import { ColorPicker } from 'primereact/colorpicker';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import { useSettings } from '../context/SettingsContext';

export default function QuickSettings({ visible, onHide }) {
  const { settings, updateSettings, resetSettings } = useSettings();

  const PRESET_COLORS = [
    { name: 'Mavi', value: '#3b82f6', colorClass: 'bg-blue-500' },
    { name: 'Zümrüt Yeşil', value: '#10b981', colorClass: 'bg-emerald-500' },
    { name: 'İndigo Rüya', value: '#6366f1', colorClass: 'bg-indigo-500' },
    { name: 'Turuncu', value: '#f97316', colorClass: 'bg-orange-500' },
    { name: 'Gül Pembesi', value: '#ec4899', colorClass: 'bg-pink-500' },
    { name: 'Asil Mor', value: '#8b5cf6', colorClass: 'bg-purple-500' },
    { name: 'Teal Okyanus', value: '#14b8a6', colorClass: 'bg-teal-500' }
  ];

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      className="md:!w-[30%] !w-full border-l border-slate-200 dark:border-slate-800"
      header={
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
            <i className="pi pi-cog text-xl text-blue-650 dark:text-blue-400 animate-spin-slow" />
          </div>
          <div>
            <h3 className="m-0 text-lg font-black text-slate-850 dark:text-slate-200">Hızlı Ayarlar</h3>
            <p className="text-[11px] text-slate-400 font-bold m-0 mt-0.5">Uygulama arayüzünü kişiselleştirin</p>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6 px-1 py-2">

        {/* Görünüm Modu */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-blue-600 rounded-full"></div>
            <h4 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest m-0">Arayüz Görünümü</h4>
          </div>
          
          <div className="flex flex-col gap-3">
            <div>
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-2">Tema Seçimi</span>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateSettings({ darkMode: false })}
                  className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 border-2 transition-all duration-200 ${!settings.darkMode ? 'bg-blue-50/20 shadow-sm border-blue-600 dark:border-blue-500' : 'bg-white dark:bg-slate-900 border-slate-250 dark:border-slate-800 hover:border-slate-350'}`}
                  style={{ borderColor: !settings.darkMode ? 'var(--primary-color)' : '' }}
                >
                  <i className="pi pi-sun text-2xl text-amber-500" />
                  <span className={`text-[11px] font-black ${!settings.darkMode ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>Açık Tema</span>
                </button>
                <button
                  onClick={() => updateSettings({ darkMode: true })}
                  className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 border-2 transition-all duration-200 ${settings.darkMode ? 'bg-blue-50/20 shadow-sm border-blue-600 dark:border-blue-500' : 'bg-white dark:bg-slate-900 border-slate-250 dark:border-slate-800 hover:border-slate-350'}`}
                  style={{ borderColor: settings.darkMode ? 'var(--primary-color)' : '' }}
                >
                  <i className="pi pi-moon text-2xl text-indigo-500" />
                  <span className={`text-[11px] font-black ${settings.darkMode ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>Karanlık Tema</span>
                </button>
              </div>
            </div>

            <div className="mt-2">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 block mb-2">Tablo Hücre Düzeni</span>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => updateSettings({ compactMode: false })}
                  className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 border-2 transition-all duration-200 ${!settings.compactMode ? 'bg-blue-50/20 shadow-sm border-blue-600 dark:border-blue-500' : 'bg-white dark:bg-slate-900 border-slate-250 dark:border-slate-800 hover:border-slate-350'}`}
                  style={{ borderColor: !settings.compactMode ? 'var(--primary-color)' : '' }}
                >
                  <i className="pi pi-align-justify text-xl text-teal-650 dark:text-teal-400" />
                  <span className={`text-[11px] font-black ${!settings.compactMode ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>Normal Sütun</span>
                </button>
                <button
                  onClick={() => updateSettings({ compactMode: true })}
                  className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 border-2 transition-all duration-200 ${settings.compactMode ? 'bg-blue-50/20 shadow-sm border-blue-600 dark:border-blue-500' : 'bg-white dark:bg-slate-900 border-slate-250 dark:border-slate-800 hover:border-slate-350'}`}
                  style={{ borderColor: settings.compactMode ? 'var(--primary-color)' : '' }}
                >
                  <i className="pi pi-bars text-xl text-purple-650 dark:text-purple-400" />
                  <span className={`text-[11px] font-black ${settings.compactMode ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'}`}>Kompakt Sütun</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <Divider className="my-1 border-slate-100 dark:border-slate-800" />

        {/* Tema Renkleri */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-slate-950 dark:bg-slate-200 rounded-full"></div>
            <h4 className="text-[11px] font-black text-slate-950 dark:text-slate-200 uppercase tracking-widest m-0">Vurgu Rengi (Accent)</h4>
          </div>

          <div>
            <span className="text-xs font-bold text-slate-950 dark:text-slate-200 block mb-2">Tema Paleti</span>
            <div className="flex flex-wrap gap-3">
              {PRESET_COLORS.map((color) => {
                const isSelected = settings.primaryColor.toLowerCase() === color.value.toLowerCase();
                return (
                  <button
                    key={color.value}
                    onClick={() => updateSettings({ primaryColor: color.value })}
                    style={{
                      backgroundColor: color.value,
                      width: '32px',
                      height: '32px',
                      minWidth: '32px',
                      minHeight: '32px',
                      borderRadius: '50%'
                    }}
                    className={`flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-sm border-2 ${isSelected ? 'border-slate-850 dark:border-white scale-105 ring-2 ring-blue-100 dark:ring-slate-800' : 'border-transparent'}`}
                    title={color.name}
                  >
                    {isSelected && <i className="pi pi-check text-white text-[10px] font-black" />}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3 mt-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full border border-slate-250" style={{ backgroundColor: settings.primaryColor }} />
                <div className="flex flex-col">
                  <span className="text-[11px] font-black text-slate-950 dark:text-slate-200">Özel Renk Tonu</span>
                  <span className="text-[10px] font-mono font-bold text-slate-400">{settings.primaryColor.toUpperCase()}</span>
                </div>
              </div>
              <ColorPicker
                value={settings.primaryColor.replace('#', '')}
                onChange={(e) => updateSettings({ primaryColor: `#${e.value}` })}
              />
            </div>
          </div>
        </section>

        <Divider className="my-1 border-slate-100 dark:border-slate-800" />

        {/* Yazı Boyutları */}
        <section className="flex flex-col gap-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1.5 h-4 bg-teal-650 rounded-full"></div>
            <h4 className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest m-0">Metin Boyutu</h4>
          </div>

          <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <i className="pi pi-text-color text-blue-500 text-base" />
                <span className="font-bold text-xs text-slate-700 dark:text-slate-350">Genel Yazı Boyutu</span>
              </div>
              <span className="bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-lg text-[10px] font-black">
                {settings.baseFontSize}px
              </span>
            </div>
            <Slider
              value={settings.baseFontSize}
              onChange={(e) => updateSettings({ baseFontSize: e.value })}
              min={10}
              max={20}
              className="my-3"
            />
            <div className="flex justify-between text-[9px] text-slate-400 font-bold px-1 mt-1">
              <span>10px</span>
              <span>15px</span>
              <span>20px</span>
            </div>
          </div>
        </section>

        <Divider className="my-1 border-slate-100 dark:border-slate-800" />

        <Button
          label="Varsayılan Ayarlara Dön"
          icon="pi pi-refresh"
          className="p-button-outlined p-button-secondary w-full rounded-2xl py-3 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200 border-slate-250 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs"
          onClick={resetSettings}
        />

        {/* Bilgilendirme */}
        <div className="bg-indigo-50/50 dark:bg-indigo-950/20 p-4 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/30 flex gap-3 mt-2">
          <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="pi pi-info-circle text-indigo-650 dark:text-indigo-400 text-base" />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-black text-indigo-900 dark:text-indigo-300">Tarayıcıda Saklanır</span>
            <p className="text-[10px] text-indigo-650 dark:text-indigo-400 m-0 leading-relaxed font-bold">
              Bu ayarlar sadece bu tarayıcıya (localStorage) özeldir. Temizlik yapıldığında sıfırlanır.
            </p>
          </div>
        </div>

      </div>
    </Sidebar>
  );
}
